// @ts-nocheck
import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Search, Trash2, Eye, Download } from 'lucide-react';
import { useLazyConsultarDocumentoQuery } from '@/services/migrate';
import StoreSelect from './store-select';
import DocumentoDetalhesModal from './documento-detalhes-modal';

const ConsultaIndividualTab = () => {
  const [cnpj, setCnpj] = useState('');
  const [serie, setSerie] = useState('');
  const [numero, setNumero] = useState('');
  const [maxTentativas, setMaxTentativas] = useState('3');
  const [modalOpen, setModalOpen] = useState(false);

  // @ts-ignore
  const [consultarDocumento, { data: resultado, isLoading, error }] = useLazyConsultarDocumentoQuery();

  const handleSearch = useCallback(async () => {
    if (!cnpj || !serie || !numero) {
      alert('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      await consultarDocumento({
        cnpj,
        serie,
        numero: Number(numero),
        maxTentativas: Number(maxTentativas),
      });
    } catch (err: any) {
      console.error('Erro ao consultar documento:', err);
    }
  }, [cnpj, serie, numero, maxTentativas, consultarDocumento]);

  const handleClearFilters = useCallback(() => {
    setCnpj('');
    setSerie('');
    setNumero('');
    setMaxTentativas('3');
  }, []);

  const handleViewDetails = useCallback(() => {
    setModalOpen(true);
  }, []);

  const isFilterEmpty = !cnpj && !serie && !numero;

  // Extrair documento dos dados de resposta
  const documento = resultado?.data?.[0]?.Documentos?.[0];

  const handleDownloadXML = useCallback(() => {
    if (documento?.DocXMLLink) {
      window.open(documento.DocXMLLink, '_blank');
    } else if (documento?.DocXML) {
      const blob = new Blob([documento.DocXML], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${documento.DocChaAcesso}.xml`;
      a.click();
      URL.revokeObjectURL(url);
    }
  }, [documento]);

  const handleConsultarEBaixar = useCallback(async () => {
    if (!cnpj || !serie || !numero) {
      alert('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      const response = await consultarDocumento({
        cnpj,
        serie,
        numero: Number(numero),
        maxTentativas: Number(maxTentativas),
      }).unwrap();

      // Extrair documento da resposta
      const doc = response?.data?.[0]?.Documentos?.[0];

      if (doc) {
        // Aguardar um pequeno delay para garantir que o documento foi processado
        setTimeout(() => {
          if (doc.DocXMLLink) {
            window.open(doc.DocXMLLink, '_blank');
          } else if (doc.DocXML) {
            const blob = new Blob([doc.DocXML], { type: 'application/xml' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${doc.DocChaAcesso}.xml`;
            a.click();
            URL.revokeObjectURL(url);
          } else {
            alert('XML não disponível para download');
          }
        }, 500);
      } else {
        alert('Documento não encontrado');
      }
    } catch (err: any) {
      console.error('Erro ao consultar documento:', err);
      alert(`Erro ao consultar: ${err?.data?.message || err.message}`);
    }
  }, [cnpj, serie, numero, maxTentativas, consultarDocumento]);

  // @ts-ignore
  return (
    <div className="space-y-6">
      {/* Filtros */}
      <Card className="p-6">
        <div className="grid grid-cols-4 gap-4 mb-4">
          <div>
            <Label htmlFor="loja">Loja / CNPJ</Label>
            <StoreSelect
              value={cnpj}
              onChange={setCnpj}
              disabled={isLoading}
            />
          </div>

          <div>
            <Label htmlFor="serie">Série</Label>
            <Input
              id="serie"
              value={serie}
              onChange={(e) => setSerie(e.target.value)}
              placeholder="1"
              disabled={isLoading}
            />
          </div>

          <div>
            <Label htmlFor="numero">Número do Documento</Label>
            <Input
              id="numero"
              type="number"
              value={numero}
              onChange={(e) => setNumero(e.target.value)}
              placeholder="123456"
              disabled={isLoading}
            />
          </div>

          <div>
            <Label htmlFor="maxTentativas">Máx. Tentativas</Label>
            <Input
              id="maxTentativas"
              type="number"
              value={maxTentativas}
              onChange={(e) => setMaxTentativas(e.target.value)}
              min={1}
              max={10}
              disabled={isLoading}
            />
          </div>
        </div>

        <div className="flex gap-2 justify-end">
          <Button
            variant="outline"
            onClick={handleClearFilters}
            disabled={isFilterEmpty || isLoading}
            className="flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Limpar
          </Button>
          <Button
            onClick={handleSearch}
            disabled={isLoading}
            className="flex items-center gap-2"
          >
            {isLoading ? (
              <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <Search className="w-4 h-4" />
            )}
            Consultar
          </Button>
          <Button
            onClick={handleConsultarEBaixar}
            disabled={isLoading}
            className="flex items-center gap-2"
          >
            {isLoading ? (
              <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <Search className="w-4 h-4" />
            )}
            Baixar XML
          </Button>
        </div>
      </Card>

      {/* Erro */}
      {error && (
        <Card className="p-6 bg-red-50 border-red-200">
          <div className="text-red-800">
            <strong>Erro:</strong> {(error as any)?.data?.message || 'Erro ao consultar documento'}
          </div>
        </Card>
      )}

      {/* Resultado */}
      {resultado && resultado.success && documento && (
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-lg">Documento Encontrado</h3>
              <div className="text-sm text-gray-600 mt-2">
                <span className="font-medium">{documento.DocModelo}</span> -
                Nº {documento.DocNumero} -
                Série {documento.DocSerie} -
                <span
                  className={`ml-2 px-2 py-1 rounded text-xs ${
                    documento.DocSitCodigo === 100
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}
                >
                  {documento.DocSitDescricao}
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              {(documento.DocXMLLink || documento.DocXML) && (
                <Button
                  onClick={handleDownloadXML}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Baixar XML
                </Button>
              )}
              <Button onClick={handleViewDetails} className="flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Ver Detalhes
              </Button>
            </div>
          </div>
        </Card>
      )}

      {resultado && resultado.success && !documento && (
        <Card className="p-12 text-center text-gray-500">
          <p>Documento não encontrado.</p>
        </Card>
      )}

      {/* Modal de Detalhes */}
      <DocumentoDetalhesModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        documento={documento}
      />
    </div>
  );
};

export default ConsultaIndividualTab;
